/**
 * Match profile jurisdiction keys (`"County - State"`) to asset `assetLocations` rows
 * when county/state strings differ by casing or US state spelling (e.g. Idaho vs ID).
 */

/** Lowercase full US state / territory name → USPS abbreviation. */
const US_STATE_NAME_TO_ABBR: Readonly<Record<string, string>> = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  "district of columbia": "DC",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  "new hampshire": "NH",
  "new jersey": "NJ",
  "new mexico": "NM",
  "new york": "NY",
  "north carolina": "NC",
  "north dakota": "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  "rhode island": "RI",
  "south carolina": "SC",
  "south dakota": "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  "west virginia": "WV",
  wisconsin: "WI",
  wyoming: "WY",
};

function parseJurisdictionKey(key: string): { county: string; state: string } | null {
  const t = key.trim();
  const idx = t.lastIndexOf(" - ");
  if (idx <= 0) return null;
  const county = t.slice(0, idx).trim();
  const state = t.slice(idx + 3).trim();
  if (!county || !state) return null;
  return { county, state };
}

/** Strip common suffixes so e.g. "Ada County" matches profile key "Ada". */
function normalizeCountyForMatch(county: string): string {
  return county
    .trim()
    .toLowerCase()
    .replace(/\s+parish\s*$/i, "")
    .replace(/\s+borough\s*$/i, "")
    .replace(/\s+county\s*$/i, "")
    .trim();
}

/** Normalize a state token to a 2-letter USPS code when possible. */
export function jurisdictionStateToAbbr(state: string): string | null {
  const s = state.trim();
  if (!s) return null;
  if (s.length === 2) return s.toUpperCase();
  return US_STATE_NAME_TO_ABBR[s.toLowerCase()] ?? null;
}

/**
 * True if this location row matches a single profile jurisdiction key
 * (same county, equivalent state).
 */
export function countyStateMatchesJurisdictionKey(
  locCounty: string,
  locState: string,
  profileKey: string,
): boolean {
  const parsed = parseJurisdictionKey(profileKey);
  if (!parsed) return false;
  if (normalizeCountyForMatch(parsed.county) !== normalizeCountyForMatch(locCounty)) return false;
  const a = jurisdictionStateToAbbr(parsed.state);
  const b = jurisdictionStateToAbbr(locState);
  if (a && b) return a === b;
  return parsed.state.trim().toLowerCase() === locState.trim().toLowerCase();
}

export function locationRowMatchesJurisdictionKeys(
  locCounty: string,
  locState: string,
  keys: ReadonlySet<string>,
): boolean {
  const compact = `${locCounty.trim()} - ${locState.trim()}`;
  if (keys.has(compact)) return true;
  const compactLc = compact.toLowerCase();
  for (const k of keys) {
    if (k.trim().toLowerCase() === compactLc) return true;
  }
  for (const key of keys) {
    if (countyStateMatchesJurisdictionKey(locCounty, locState, key)) return true;
  }
  return false;
}
